import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { login as apiLogin, logout as apiLogout } from '@/api/auth'
import type { LoginPayload, User } from '@/types/user'

/**
 * 刷新页面后从 localStorage 恢复 user，避免管理员身份丢失
 * （token 有恢复而 user 没有，会导致刷新后 isAdmin=false、管理入口消失）。
 */
function restoreUser(): User | null {
  try {
    const raw = localStorage.getItem('user')
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? (parsed as User) : null
  } catch {
    localStorage.removeItem('user')
    return null
  }
}

/**
 * 用户状态：token 持久化到 localStorage，user 缓存当前登录用户。
 */
export const useUserStore = defineStore('user', () => {
  const user = ref<User | null>(restoreUser())
  const token = ref<string | null>(localStorage.getItem('token'))

  const isAuthenticated = computed(() => !!token.value)
  // App.vue / 路由守卫中使用的别名
  const isLoggedIn = computed(() => !!token.value)
  const isAdmin = computed(() => {
    const u = user.value
    if (!u) return false
    return u.is_admin === true
  })

  function setUser(userData: User | null) {
    user.value = userData
    if (userData) {
      localStorage.setItem('user', JSON.stringify(userData))
    } else {
      localStorage.removeItem('user')
    }
  }

  function setToken(newToken: string | null) {
    token.value = newToken
    if (newToken) {
      localStorage.setItem('token', newToken)
    } else {
      localStorage.removeItem('token')
    }
  }

  function clearSession() {
    setToken(null)
    setUser(null)
  }

  async function login(credentials: LoginPayload) {
    try {
      const response = await apiLogin(credentials)
      setToken(response.access_token)
      setUser(response.user)
      return { success: true as const }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : '登录失败'
      return { success: false as const, message }
    }
  }

  async function logout() {
    try {
      await apiLogout()
    } catch {
      // 即使后端登出失败，也清空本地会话
    } finally {
      clearSession()
    }
  }

  return {
    user,
    token,
    isAuthenticated,
    isLoggedIn,
    isAdmin,
    setUser,
    setToken,
    clearSession,
    login,
    logout
  }
})
